"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonLoading,
} from "@ionic/react"
import { addOutline } from "ionicons/icons"
import { useHistory } from "react-router-dom"
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Chip,
  Badge,
  Grid,
  TextField,
  InputAdornment,
  Fab,
  Avatar,
  Stack,
  Paper,
  Alert,
  useTheme,
  useMediaQuery,
  Container,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material"
import {
  Search as SearchIcon,
  Add as AddIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Tag as TagIcon,
  FolderOpen as FolderIcon,
  Business as BusinessIcon,
  Clear as ClearIcon,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import { getProjects ,getProjectsByAssingment} from "../../../api/projects/project"

interface Project {
  id: number
  project_id: string
  project_name: string
  tags: string
  budget_cost: number | null
  status: "PENDING" | "IN_PROGRESS" | "REVIEW" | "COMPLETED" | "ON_HOLD"
  user_id: string
  business_id: string | null
  created_at: string
  updated_at: string
}

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
    borderColor: theme.palette.primary.light,
  },
  "&:active": {
    transform: "translateY(-2px)",
  },
  margin: theme.spacing(0.5),
  height: "100%",
  overflow: "visible",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: "16px 16px 0 0",
  },
}))

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case "PENDING":
        return {
          backgroundColor: "#f8fafc",
          color: "#64748b",
          border: "1px solid #e2e8f0",
        }
      case "IN_PROGRESS":
        return {
          backgroundColor: "#eff6ff",
          color: "#2563eb",
          border: "1px solid #93c5fd",
        }
      case "REVIEW":
        return {
          backgroundColor: "#fefce8",
          color: "#ca8a04",
          border: "1px solid #fde047",
        }
      case "COMPLETED":
        return {
          backgroundColor: "#f0fdf4",
          color: "#16a34a",
          border: "1px solid #86efac",
        }
      case "ON_HOLD":
        return {
          backgroundColor: "#fef2f2",
          color: "#dc2626",
          border: "1px solid #fca5a5",
        }
      default:
        return {
          backgroundColor: "#f8fafc",
          color: "#64748b",
          border: "1px solid #e2e8f0",
        }
    }
  }
  return {
    ...getStatusColor(),
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 28,
    borderRadius: 14,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  }
})

const SearchContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  borderRadius: 20,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
}))

const TabPanel = styled(Box)({
  padding: 0,
})

interface ProjectCardProps {
  project: Project
  onProjectClick: (project: Project) => void
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onProjectClick }) => {
  const parseTags = (tagsString: string): string[] => {
    try {
      if (!tagsString) return []
      const cleanedString = tagsString.replace(/^"|"$/g, "")
      return cleanedString
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
    } catch (error) {
      console.error("Error parsing tags:", error)
      return []
    }
  }

  const formatCurrency = (amount: number | null): string => {
    if (amount === null || amount === undefined) {
      return "Budget TBD"
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const formatStatus = (status: string): string => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
  }

  const projectTags = parseTags(project.tags)

  return (
    <StyledCard onClick={() => onProjectClick(project)}>
      <CardHeader
        avatar={
          <Avatar
            sx={{
              bgcolor: "primary.main",
              width: 44,
              height: 44,
              fontSize: "1.2rem",
              fontWeight: 700,
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
            }}
          >
            {project.project_name.charAt(0).toUpperCase()}
          </Avatar>
        }
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <StatusChip label={formatStatus(project.status)} status={project.status} size="small" />
          </Box>
        }
        title={
          <Typography variant="h6" sx={{ fontSize: "1.15rem", fontWeight: 700, color: "text.primary", mb: 0.5 }}>
            {project.project_name}
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
            {/* ID: {project.project_id} */}
          </Typography>
        }
        sx={{ pb: 1.5 }}
      />
      <CardContent sx={{ pt: 0, pb: 2.5 }}>
        <Stack spacing={2.5}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
              {/* <MoneyIcon fontSize="small" sx={{ color: "success.main" }} /> */}
              <Typography
                variant="body1"
                color={project.budget_cost ? "success.main" : "text.secondary"}
                sx={{ fontWeight: 700, fontSize: "1.05rem" }}
              >
                {formatCurrency(project.budget_cost)}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
              <CalendarIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                Updated: {formatDate(project.updated_at)}
              </Typography>
            </Stack>
            {project.business_id && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <BusinessIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                  Business: {project.business_id}
                </Typography>
              </Stack>
            )}
          </Box>

          {projectTags.length > 0 && (
            <Box>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {projectTags.slice(0, 2).map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    variant="outlined"
                    icon={<TagIcon />}
                    sx={{
                      fontSize: "0.7rem",
                      height: 26,
                      borderRadius: 13,
                      backgroundColor: "background.default",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "primary.light",
                        color: "primary.contrastText",
                      },
                    }}
                  />
                ))}
                {projectTags.length > 2 && (
                  <Chip
                    label={`+${projectTags.length - 2}`}
                    size="small"
                    variant="filled"
                    sx={{
                      fontSize: "0.7rem",
                      height: 26,
                      borderRadius: 13,
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      fontWeight: 700,
                    }}
                  />
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>
    </StyledCard>
  )
}

const ATSProjects_staff: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchText, setSearchText] = useState("")
  const [selectedTab, setSelectedTab] = useState(0)
  const history = useHistory()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const fetchProjects = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await getProjectsByAssingment()
      console.log("Projects response:", response.data)

      if (Array.isArray(response.data)) {
        setProjects(response.data)
      } else {
        throw new Error("Invalid response format")
      }
    } catch (error) {
      console.error("Error fetching projects:", error)
      setError("Failed to load projects. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProjects()
  }, [])

  const handleProjectClick = (project: Project) => {
    history.push(`/folder/projects/${project.project_id}`)
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
  }

  const filteredProjects = projects?.filter((project) => {
    const searchLower = searchText?.toLowerCase()
    const projectNameMatch = project.project_name.toLowerCase().includes(searchLower)
    const parseTags = (tagsString: string): string[] => {
      try {
        if (!tagsString) return []
        const cleanedString = tagsString.replace(/^"|"$/g, "")
        return cleanedString
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag.length > 0)
      } catch (error) {
        return []
      }
    }
    const tagsMatch = parseTags(project.tags).some((tag) => tag?.toLowerCase().includes(searchLower))
    const projectIdMatch = project.project_id?.toLowerCase().includes(searchLower)
    return projectNameMatch || tagsMatch || projectIdMatch
  })

  const statusTabs = [
    { label: "All Projects", value: "ALL" },
    { label: "Pending", value: "PENDING" },
    { label: "In Progress", value: "IN_PROGRESS" },
    { label: "Review", value: "REVIEW" },
    { label: "Completed", value: "COMPLETED" },
    { label: "On Hold", value: "ON_HOLD" },
  ]

  const getProjectsForTab = () => {
    if (selectedTab === 0) return filteredProjects // All projects
    const selectedStatus = statusTabs[selectedTab].value
    return filteredProjects.filter((project) => project.status === selectedStatus)
  }

  const handleAddProject = () => {
    history.push("/projects/new")
  }

  const handleRetry = () => {
    fetchProjects()
  }

  const clearSearch = () => {
    setSearchText("")
  }

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar color="primary">
          <IonTitle>Project Management</IonTitle>
          {/* <IonButtons slot="end">
            <IonButton onClick={handleAddProject}>
              <IonIcon icon={addOutline} />
              New Project
            </IonButton>
          </IonButtons> */}
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 4, borderRadius: 3, boxShadow: "0 4px 12px rgba(211, 47, 47, 0.15)" }}
              action={
                <IonButton fill="clear" onClick={handleRetry}>
                  Retry
                </IonButton>
              }
            >
              {error}
            </Alert>
          )}

          <SearchContainer elevation={0}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search projects, tags, or project ID..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearch} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 4,
                  backgroundColor: "background.default",
                  fontSize: "1rem",
                  "&:hover": {
                    backgroundColor: "background.paper",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "background.paper",
                  },
                },
              }}
            />
          </SearchContainer>

          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 4 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="project status tabs"
            >
              {statusTabs.map((tab, index) => (
                <Tab
                  key={tab.value}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {tab.label}
                      {index === 0 ? (
                        <Badge
                          badgeContent={filteredProjects.length}
                          color="primary"
                          sx={{ ml: 1 }}
                        />
                      ) : (
                        <Badge
                          badgeContent={filteredProjects.filter(p => p.status === tab.value).length}
                          color="primary"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {projects?.length === 0 && !loading && !error && (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <FolderIcon sx={{ fontSize: 100, color: "text.disabled", mb: 3 }} />
              <Typography variant="h4" color="text.secondary" gutterBottom sx={{ fontWeight: 700 }}>
                No projects found
              </Typography>
              {/* <Typography variant="body1" color="text.secondary" sx={{ mb: 5, fontSize: "1.1rem" }}>
                Get started by creating your first project
              </Typography> */}
              {/* <IonButton onClick={handleAddProject} size="large">
                <IonIcon icon={addOutline} slot="start" />
                Create Project
              </IonButton> */}
            </Box>
          )}

          {projects.length > 0 && (
            <TabPanel>
              <Grid container spacing={3}>
                {getProjectsForTab().map((project) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={project.id}>
                    <ProjectCard project={project} onProjectClick={handleProjectClick} />
                  </Grid>
                ))}
              </Grid>
              
              {getProjectsForTab().length === 0 && (
                <Box sx={{ textAlign: "center", py: 10 }}>
                  <FolderIcon sx={{ fontSize: 80, color: "text.disabled", mb: 3 }} />
                  <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                    No projects in this category
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 5 }}>
                    {selectedTab === 0 
                      ? "Try adjusting your search terms" 
                      : `No projects with status "${statusTabs[selectedTab].label}"`}
                  </Typography>
                </Box>
              )}
            </TabPanel>
          )}
        </Container>

        {/* <Fab
          color="primary"
          aria-label="add"
          onClick={handleAddProject}
          sx={{
            position: "fixed",
            bottom: 32,
            right: 32,
            width: 72,
            height: 72,
            boxShadow: "0 12px 40px rgba(25, 118, 210, 0.3)",
            "&:hover": {
              transform: "scale(1.1)",
              boxShadow: "0 16px 48px rgba(25, 118, 210, 0.4)",
            },
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <AddIcon sx={{ fontSize: 32 }} />
        </Fab> */}

        <IonLoading isOpen={loading} message="Loading projects..." />
      </IonContent>
    </IonPage>
  )
}

export default ATSProjects_staff